import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraButton, SpecraEntityCell, SpecraFormField, SpecraInput, SpecraText, SpecraTooltip } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../../../contexts/authContext';
import { useTenantAreas } from '../../../../lib/access/useTenantAreas';
import { artifactClient } from '../../../../lib/connect/clients';
import { formatMoneyCompact } from '../../../../lib/format/money';
import { AwardFollowOn } from '../../../Opportunities/Signals/AwardFollowOn';
import { RegistryListNote } from '../../../Registries/list/RegistryListNote';
import { useContractSummaries } from '../../../Registries/list/useRegistrySummaries';
import { useRegistryRecordsEnabled } from '../../../Registries/registryFlags';
import { registryHref } from '../../../Registries/registryHref';
import { EntityTable } from '../../_shared/components/EntityTable';
import { useRegistryQuery, useRegistryRecord } from '../registryParams';
import { contractsClient, loadAssociationOptions } from './api';
import { identityLabel, keyLabel, safeSourceUrl, valueLabel } from './facts';
import { ContractForm, FactForm } from './forms';
export const contractQueryKey = (viewer, ...parts)=>[
        'registry-contracts',
        viewer.tenant,
        viewer.user,
        ...parts
    ];
const ids = Testids.registryContracts;
export function ContractsTab() {
    const { user } = useAuth();
    if (!user?.tenant_id || !user.id) return /*#__PURE__*/ _jsx("output", {
        children: "Sign in to browse contracts."
    });
    const viewer = {
        tenant: user.tenant_id,
        user: user.id
    };
    // Remount forms, evidence selection and local state synchronously when the
    // identity changes. No previous-data placeholders may cross this boundary.
    return /*#__PURE__*/ _jsx(ContractsWorkspace, {
        viewer: viewer
    }, `${viewer.tenant}:${viewer.user}`);
}
function ContractsWorkspace({ viewer }) {
    const [query, setQuery] = useRegistryQuery();
    const { recordId, openRecord, closeRecord } = useRegistryRecord();
    const { registries: canEdit } = useTenantAreas();
    const [creating, setCreating] = useState(false);
    const browse = useInfiniteQuery({
        queryKey: contractQueryKey(viewer, 'browse', query),
        queryFn: ({ pageParam })=>contractsClient.browseRegistryContracts({
                piid: query,
                afterId: pageParam,
                limit: 30
            }),
        initialPageParam: '',
        getNextPageParam: (last)=>last.nextAfterId || undefined,
        placeholderData: undefined,
        retry: false
    });
    const recordsOn = useRegistryRecordsEnabled();
    const rows = browse.data?.pages.flatMap((page)=>page.contracts) ?? [];
    const summaries = useContractSummaries(recordsOn && !recordId ? rows.map((row)=>row.id) : []);
    const byId = new Map((summaries.isError ? [] : summaries.data ?? []).map((s)=>[
            s.contractId,
            s
        ]));
    const location = useLocation();
    const origin = location.pathname + location.search;
    const refLink = (ref)=>{
        if (!ref) return '—';
        const href = registryHref(ref, origin);
        return href ? /*#__PURE__*/ _jsx(Link, {
            to: href,
            children: ref.name || '—'
        }) : ref.name || '—';
    };
    const columns = [
        {
            key: 'contract',
            header: 'Contract',
            render: (c)=>/*#__PURE__*/ _jsx(SpecraEntityCell, {
                    title: c.identity?.piid || '—',
                    description: c.identity?.piid
                })
        },
        {
            key: 'holder',
            header: 'Holder',
            render: (c)=>{
                const s = byId.get(c.id);
                if (!s) return '—';
                return /*#__PURE__*/ _jsx(SpecraEntityCell, {
                    title: s.ourRole === 'PRIME' ? 'Us' : refLink(s.holder),
                    description: s.ourRole === 'PRIME' ? 'prime' : s.ourRole === 'SUB' ? `we sub · ${s.ourVisibleSeatCount} seats` : undefined
                });
            }
        },
        {
            key: 'customer',
            header: 'Customer',
            render: (c)=>refLink(byId.get(c.id)?.customer)
        },
        {
            key: 'vehicle',
            header: 'Vehicle',
            render: (c)=>refLink(byId.get(c.id)?.vehicle)
        },
        {
            key: 'value',
            header: 'Value',
            align: 'right',
            render: (c)=>{
                const value = byId.get(c.id)?.value;
                return /*#__PURE__*/ _jsx(SpecraTooltip, {
                    content: value ? 'Obligations to date — award value, not a labor rate' : 'No award value recorded yet',
                    children: /*#__PURE__*/ _jsx(SpecraText, {
                        as: "span",
                        size: "body-ui",
                        children: value ? formatMoneyCompact(value.amount * 100) : '—'
                    })
                });
            }
        },
        {
            key: 'ends',
            header: 'Ends',
            render: (c)=>{
                const end = byId.get(c.id)?.popEnd;
                return end ? format(end.toDate(), 'MMM yyyy') : '—';
            }
        },
        {
            key: 'nextRound',
            header: 'Next round',
            render: (c)=>{
                const next = byId.get(c.id)?.nextRound;
                if (next?.pursuit) return /*#__PURE__*/ _jsx(SpecraEntityCell, {
                    title: refLink(next.pursuit),
                    description: next.pursuitStage
                });
                return next?.expectedOpenAt ? /*#__PURE__*/ _jsx(SpecraEntityCell, {
                    title: "No pursuit",
                    description: `opens ~${format(next.expectedOpenAt.toDate(), 'MMM yyyy')}`
                }) : '—';
            }
        }
    ];
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-4",
        "aria-label": "Contracts",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-end gap-3",
                children: [
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        className: "flex-1",
                        label: "Filter by PIID",
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            "data-testid": ids.search,
                            value: query,
                            onChange: (e)=>setQuery(e.target.value)
                        })
                    }),
                    canEdit && /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": ids.create,
                        onClick: ()=>setCreating(true),
                        children: "New contract"
                    })
                ]
            }),
            creating && canEdit && /*#__PURE__*/ _jsx(ContractForm, {
                viewer: viewer,
                onCancel: ()=>setCreating(false),
                onSaved: (c)=>{
                    setCreating(false);
                    void browse.refetch();
                    openRecord(c.id);
                }
            }),
            recordId ? /*#__PURE__*/ _jsx(ContractDetail, {
                viewer: viewer,
                id: recordId,
                canEdit: canEdit,
                onBack: closeRecord
            }, recordId) : /*#__PURE__*/ _jsx("div", {
                "data-testid": ids.browse,
                "aria-busy": browse.isFetching,
                children: browse.isPending ? /*#__PURE__*/ _jsx("output", {
                    "data-testid": ids.status,
                    children: "Loading contract identities…"
                }) : browse.isError ? /*#__PURE__*/ _jsx(Unavailable, {
                    retry: ()=>void browse.refetch(),
                    text: "Contracts unavailable. Your access or connection may have changed."
                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx("p", {
                            children: browse.data.pages[0]?.total === undefined ? 'Total unknown' : `${browse.data.pages[0].total.toString()} contracts`
                        }),
                        recordsOn ? rows.length > 0 && /*#__PURE__*/ _jsx(EntityTable, {
                            data: rows,
                            columns: columns,
                            testid: ids.table,
                            rowTestid: ids.row,
                            rowKey: (c)=>c.id,
                            getRowLabel: (c)=>identityLabel(c.identity),
                            onRowClick: (c)=>openRecord(c.id)
                        }) : browse.data.pages.flatMap((p)=>p.contracts).map((c)=>/*#__PURE__*/ _jsx("div", {
                                "data-testid": ids.row,
                                className: "border-b py-3",
                                children: /*#__PURE__*/ _jsx(SpecraButton, {
                                    variant: "ghost",
                                    onClick: ()=>openRecord(c.id),
                                    children: identityLabel(c.identity)
                                })
                            }, c.id)),
                        recordsOn && /*#__PURE__*/ _jsx(RegistryListNote, {
                            children: "Every contract we know about, ours or not. Each one is a round of a need the government keeps buying."
                        }),
                        !browse.data.pages[0]?.contracts.length && /*#__PURE__*/ _jsxs("p", {
                            children: [
                                "No visible contracts match this PIID. Try a different filter",
                                canEdit ? ' or create a contract with its full identity context.' : '.'
                            ]
                        }),
                        browse.hasNextPage && /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": ids.more,
                            disabled: browse.isFetchingNextPage,
                            onClick: ()=>void browse.fetchNextPage(),
                            children: browse.isFetchingNextPage ? 'Loading more…' : 'More results'
                        })
                    ]
                })
            })
        ]
    });
}
function Unavailable({ text, retry }) {
    return /*#__PURE__*/ _jsxs("div", {
        role: "alert",
        "data-testid": ids.status,
        children: [
            /*#__PURE__*/ _jsx("p", {
                children: text
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "secondary",
                onClick: retry,
                children: "Retry"
            })
        ]
    });
}
export function useAssociations(viewer) {
    return useQuery({
        queryKey: contractQueryKey(viewer, 'associations'),
        queryFn: ()=>loadAssociationOptions(viewer.tenant),
        placeholderData: undefined,
        retry: false
    });
}
function ContractDetail({ viewer, id, canEdit, onBack }) {
    const [review, setReview] = useState(false);
    const [editRefs, setEditRefs] = useState(false);
    const [source, setSource] = useState();
    const detail = useQuery({
        queryKey: contractQueryKey(viewer, 'detail', id),
        queryFn: ()=>contractsClient.getRegistryContract({
                id
            }),
        placeholderData: undefined,
        retry: false
    });
    const catalog = useAssociations(viewer);
    const evidence = useQuery({
        queryKey: contractQueryKey(viewer, 'evidence', id),
        enabled: !!detail.data?.contract && !detail.isError,
        placeholderData: undefined,
        retry: false,
        queryFn: async ()=>{
            const rows = [];
            const seen = new Set();
            let afterId = '';
            do {
                const page = await contractsClient.listRegistryContractEvidence({
                    contractId: id,
                    afterId,
                    limit: 100
                });
                rows.push(...page.evidence);
                afterId = page.nextAfterId;
                if (afterId && seen.has(afterId)) throw new Error('Evidence pagination did not advance');
                seen.add(afterId);
            }while (afterId)
            return rows;
        }
    });
    const c = detail.isError ? undefined : detail.data?.contract;
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-4 rounded-lg border p-4",
        "data-testid": ids.detail,
        children: [
            /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "secondary",
                "data-testid": ids.back,
                onClick: onBack,
                children: "Back to contracts"
            }),
            detail.isPending ? /*#__PURE__*/ _jsx("output", {
                children: "Loading contract context…"
            }) : !c ? /*#__PURE__*/ _jsx(Unavailable, {
                text: "Contract unavailable. It may not exist or you may not have access. Return to contracts or retry.",
                retry: ()=>void detail.refetch()
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("h2", {
                        className: "text-lg font-semibold",
                        children: identityLabel(c.identity)
                    }),
                    /*#__PURE__*/ _jsx(AwardFollowOn, {
                        contractId: id,
                        label: identityLabel(c.identity),
                        keys: (evidence.data ?? []).flatMap((e)=>e.key ? [
                                e.key
                            ] : [])
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        children: "Identity is contextual: PIID alone does not establish a match. Unresolved fields need source context."
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        children: "Associations are manually selected; holder eligibility and current procurement availability are not verified."
                    }),
                    catalog.isPending ? /*#__PURE__*/ _jsx("output", {
                        children: "Loading linked registry names…"
                    }) : catalog.isError ? /*#__PURE__*/ _jsx(Unavailable, {
                        text: "Linked registry names unavailable.",
                        retry: ()=>void catalog.refetch()
                    }) : /*#__PURE__*/ _jsx(Associations, {
                        contract: c,
                        options: catalog.data,
                        viewer: viewer
                    }),
                    canEdit && /*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "secondary",
                                onClick: ()=>setEditRefs(!editRefs),
                                children: "Edit associations"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": ids.review,
                                onClick: ()=>setReview(!review),
                                children: "Review a fact"
                            })
                        ]
                    }),
                    editRefs && canEdit && /*#__PURE__*/ _jsx(ContractForm, {
                        viewer: viewer,
                        contract: c,
                        onCancel: ()=>setEditRefs(false),
                        onSaved: ()=>{
                            setEditRefs(false);
                            void detail.refetch();
                        }
                    }, c.id),
                    review && canEdit && /*#__PURE__*/ _jsx(FactForm, {
                        viewer: viewer,
                        contractId: id,
                        onCancel: ()=>setReview(false),
                        onSaved: ()=>{
                            setReview(false);
                            void evidence.refetch();
                        }
                    }),
                    /*#__PURE__*/ _jsx("h3", {
                        className: "font-semibold",
                        children: "Scoped facts and competing originals"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        children: "Public prime facts describe the prime award. Reviewed subcontract facts describe only the named workshare, measure and period. Proposals never become current facts."
                    }),
                    evidence.isPending ? /*#__PURE__*/ _jsx("output", {
                        children: "Loading evidence…"
                    }) : evidence.isError ? /*#__PURE__*/ _jsx(Unavailable, {
                        text: "Evidence unavailable. No selected values can be confirmed.",
                        retry: ()=>void evidence.refetch()
                    }) : /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            !evidence.data.length && /*#__PURE__*/ _jsx("p", {
                                children: "No visible evidence. Values are unknown; this does not prove no award exists."
                            }),
                            evidence.data.map((e)=>/*#__PURE__*/ _jsx(FactRow, {
                                    viewer: viewer,
                                    contractId: id,
                                    evidence: e,
                                    onSource: setSource
                                }, e.id))
                        ]
                    }),
                    source && !evidence.isError && /*#__PURE__*/ _jsx(EvidencePanel, {
                        viewer: viewer,
                        observation: source,
                        onBack: ()=>setSource(undefined)
                    }, source.id)
                ]
            })
        ]
    });
}
function Associations({ contract: c, options, viewer }) {
    const location = useLocation();
    const parent = useQuery({
        queryKey: contractQueryKey(viewer, 'parent', c.refs?.parentId),
        queryFn: ()=>contractsClient.getRegistryContract({
                id: c.refs?.parentId
            }),
        enabled: !!c.refs?.parentId,
        placeholderData: undefined,
        retry: false
    });
    const returnTo = location.pathname + location.search;
    const linked = (label, id, list, tab)=>{
        const option = list.find((o)=>o.id === id);
        return /*#__PURE__*/ _jsxs("div", {
            children: [
                /*#__PURE__*/ _jsxs("span", {
                    children: [
                        label,
                        ": "
                    ]
                }),
                !id ? 'Unresolved / no association' : !option ? 'Linked record unavailable' : /*#__PURE__*/ _jsx(Link, {
                    className: "underline",
                    to: `/registries?${new URLSearchParams({
                        tab,
                        ...tab === 'lcats' ? {} : {
                            record: option.recordId || id
                        },
                        returnTo
                    })}`,
                    children: option.label
                })
            ]
        }, label);
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2",
        children: [
            linked('Awardee', c.refs?.companyId, options.companies, 'companies'),
            linked('Awarding organization', c.refs?.governmentOrgId, options.agencies, 'govorgs'),
            linked('Vehicle mechanism', c.refs?.vehicleId, options.vehicles, 'vehicles'),
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    "Parent contract:",
                    ' ',
                    c.refs?.parentId ? parent.data?.contract && !parent.isError ? /*#__PURE__*/ _jsx(Link, {
                        className: "underline",
                        to: `/registries?${new URLSearchParams({
                            tab: 'contracts',
                            record: c.refs.parentId,
                            returnTo
                        })}`,
                        children: identityLabel(parent.data.contract.identity)
                    }) : 'Parent unavailable / loading' : 'Unresolved / no parent'
                ]
            }),
            linked('Associated rate (Labor Categories)', c.refs?.rateObservationId, c.associatedRate ? [
                {
                    id: c.associatedRate.id,
                    recordId: c.associatedRate.lcatEntryId,
                    label: c.associatedRate.label
                }
            ] : options.rates, 'lcats'),
            c.refs?.rateObservationId && /*#__PURE__*/ _jsx("p", {
                children: "In Labor Categories, open the named category and its Rates section to inspect the associated observation. The link opens the existing owning registry."
            })
        ]
    });
}
function FactRow({ viewer, contractId, evidence: e, onSource }) {
    const observation = useQuery({
        queryKey: contractQueryKey(viewer, 'observation', e.observationId),
        queryFn: ()=>contractsClient.getRegistryContractObservation({
                id: e.observationId
            }),
        enabled: !!e.observationId,
        placeholderData: undefined,
        retry: false
    });
    const selected = useQuery({
        queryKey: contractQueryKey(viewer, 'selected', contractId, e.key),
        queryFn: ()=>contractsClient.resolveRegistryContractFact({
                contractId,
                key: e.key
            }),
        placeholderData: undefined,
        retry: false
    });
    const o = observation.isError ? undefined : observation.data?.observation;
    return /*#__PURE__*/ _jsxs("article", {
        className: "space-y-2 border-t py-3",
        children: [
            /*#__PURE__*/ _jsx("h4", {
                children: keyLabel(e.key)
            }),
            /*#__PURE__*/ _jsx("p", {
                children: e.proposal ? 'Proposal — never current' : 'Recorded evidence'
            }),
            /*#__PURE__*/ _jsx("p", {
                children: selected.isPending ? 'Resolving selected value…' : selected.isError ? selected.error instanceof ConnectError && selected.error.code === Code.NotFound ? 'Selected: Unknown' : 'Selected value unavailable — retrieval failed' : `Selected: ${valueLabel(selected.data.observation?.fact, e.key?.unit)}`
            }),
            /*#__PURE__*/ _jsx("p", {
                children: observation.isPending ? 'Loading original…' : !o ? 'Original unavailable' : `Original: ${valueLabel(o.fact, o.key?.unit)} · ${o.source || 'Unknown source'}`
            }),
            !selected.isPending && !selected.isError && selected.data?.observation && /*#__PURE__*/ _jsxs("p", {
                children: [
                    "Selected basis: ",
                    selected.data.observation.source || 'Unknown source',
                    " · Effective as of:",
                    ' ',
                    selected.data.observation.effectiveAt?.toDate().toISOString() || 'Unknown',
                    " · Retrieved:",
                    ' ',
                    selected.data.observation.retrievedAt?.toDate().toISOString() || 'Unknown'
                ]
            }),
            o && /*#__PURE__*/ _jsxs("p", {
                children: [
                    "Original effective as of: ",
                    o.effectiveAt?.toDate().toISOString() || 'Unknown',
                    " · Retrieved: ",
                    o.retrievedAt?.toDate().toISOString() || 'Unknown'
                ]
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    "Reviewed by ",
                    e.actor || 'Unknown actor',
                    " ·",
                    ' ',
                    e.createdAt?.toDate().toISOString() || 'Unknown review date',
                    " ·",
                    ' ',
                    e.rationale || 'No rationale available'
                ]
            }),
            o && /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "secondary",
                "data-testid": ids.source,
                onClick: ()=>onSource(o),
                children: "Source evidence"
            })
        ]
    });
}
function EvidencePanel({ viewer, observation: o, onBack }) {
    const url = safeSourceUrl(o.sourceLocation);
    const document = useQuery({
        queryKey: contractQueryKey(viewer, 'document-source', o.artifactId, o.artifactVersionId),
        queryFn: ()=>artifactClient.generateDownloadUrl({
                artifactId: o.artifactId
            }),
        enabled: !!o.artifactId,
        placeholderData: undefined,
        retry: false
    });
    const documentUrl = !document.isError && document.data?.url ? safeSourceUrl(document.data.url) : undefined;
    return /*#__PURE__*/ _jsxs("aside", {
        className: "space-y-2 rounded border p-4",
        "data-testid": ids.evidence,
        "aria-label": "Source evidence",
        children: [
            /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "secondary",
                onClick: onBack,
                children: "Back to facts"
            }),
            /*#__PURE__*/ _jsxs("h3", {
                children: [
                    "Source: ",
                    o.source || 'Unknown source'
                ]
            }),
            /*#__PURE__*/ _jsx("p", {
                children: identityLabel(o.identity)
            }),
            /*#__PURE__*/ _jsx("p", {
                children: keyLabel(o.key)
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    "Effective as of: ",
                    o.effectiveAt?.toDate().toISOString() || 'Unknown',
                    " · Retrieved:",
                    ' ',
                    o.retrievedAt?.toDate().toISOString() || 'Unknown'
                ]
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    "Version: ",
                    o.sourceVersion || 'Unknown',
                    " · Record: ",
                    o.sourceRecordId || 'Unknown',
                    " · Action:",
                    ' ',
                    o.actionId || 'Unknown',
                    " · Modification: ",
                    o.modificationId || 'Unknown'
                ]
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    "Location: ",
                    o.sourceLocation || 'Unknown',
                    " · Page: ",
                    o.page || 'Unknown',
                    " · Section:",
                    ' ',
                    o.section || 'Unknown'
                ]
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    "Artifact: ",
                    o.artifactId || 'None',
                    " · Version: ",
                    o.artifactVersionId || 'Unknown'
                ]
            }),
            /*#__PURE__*/ _jsx("blockquote", {
                children: o.quote
            }),
            /*#__PURE__*/ _jsx("pre", {
                className: "whitespace-pre-wrap break-words",
                children: o.originalJson || 'Original source text unavailable'
            }),
            url && /*#__PURE__*/ _jsx("a", {
                className: "underline",
                href: url,
                target: "_blank",
                rel: "noopener noreferrer",
                children: "Open external source"
            }),
            o.artifactId && (documentUrl ? /*#__PURE__*/ _jsx("a", {
                className: "underline",
                href: documentUrl,
                target: "_blank",
                rel: "noopener noreferrer",
                children: "Open permitted document (current artifact)"
            }) : /*#__PURE__*/ _jsx("p", {
                children: document.isPending ? 'Checking document permission…' : 'Document unavailable or access denied'
            }))
        ]
    });
}
